import React, { useState, useEffect } from "react";
import { decryptEmail, ENCRYPTED_EMAIL } from "../../utils/emailEncryption";
import styles from "./EmailDecryptModal.module.css";

interface EmailDecryptModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EmailDecryptModal: React.FC<EmailDecryptModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isDecrypted, setIsDecrypted] = useState(false);
  const [email, setEmail] = useState(decryptEmail(ENCRYPTED_EMAIL));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const handleCopy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setIsDecrypted(false);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close modal"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Contact via Email</h2>
          <p className={styles.modalSubtitle}>
            Click the button below to reveal our email address
          </p>
        </div>

        <div className={styles.emailContainer}>
          <div className={styles.emailDisplay}>
            <span className={styles.emailText}>{email}</span>
          </div>

          <div className={styles.buttonGroup}>
            <button className={styles.copyButton} onClick={handleCopy}>
              {copied ? "Copied!" : "Copy Email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
